import { z } from 'zod';

/**
 * Universal search: one box that finds anything the person looking is allowed
 * to see.
 *
 * Two rules shape the whole feature, and both are about access rather than
 * relevance.
 *
 * **A hit is a disclosure.** A row's mere EXISTENCE is information — that a
 * chassis called "gachie1" exists, that a subscriber called "Kiarie" is on
 * somebody's fleet. So every source is gated twice: by the capability that
 * governs its own page, and by the org the caller is scoped to. A viewer
 * without `olt.read` does not get a filtered list of chassis, they get no
 * chassis type in the answer at all — the same "not found" the routes give,
 * because a search that can confirm a name exists is an existence oracle
 * across tenants.
 *
 * **Secrets are not searchable, at all.** Not "redacted in the result" —
 * never read. API key tokens, encrypted credentials and the values in
 * `platform_settings` (which hold the M-Pesa passkey and the object-store
 * secret) are excluded by construction: a search that matched them would let
 * somebody confirm a secret's value one guessed prefix at a time.
 */

/**
 * What a hit IS. The list is closed so the client can order and icon them
 * without a lookup table that drifts from the server's.
 */
export const SEARCH_HIT_TYPES = [
  'onu',
  'olt',
  'device',
  'vlan',
  'zone',
  'splitter',
  'odf',
  'gateway',
  'authorize_preset',
  'device_preset',
  'speed_profile',
  'onu_type',
  'user',
  'invoice',
  'api_key',
  'organization',
  /** Not a row: a page or settings tab the caller may open. */
  'destination',
] as const;
export type SearchHitType = (typeof SEARCH_HIT_TYPES)[number];

export const SEARCH_TYPE_LABELS: Record<SearchHitType, string> = {
  onu: 'ONUs',
  olt: 'OLTs',
  device: 'CPE devices',
  vlan: 'VLANs',
  zone: 'Zones',
  splitter: 'Splitters',
  odf: 'ODF',
  gateway: 'Gateways',
  authorize_preset: 'Authorise presets',
  device_preset: 'Device presets',
  speed_profile: 'Speed profiles',
  onu_type: 'ONU types',
  user: 'Users',
  invoice: 'Invoices',
  api_key: 'API keys',
  organization: 'Organizations',
  destination: 'Go to',
};

export const searchHitSchema = z.object({
  type: z.enum(SEARCH_HIT_TYPES),
  /** Row id, or the path for a destination. Unique within a type. */
  id: z.string(),
  /** What the thing is called — the line the operator scans. */
  title: z.string(),
  /** Where it is, or what it is: the chassis, the org, the interface. */
  subtitle: z.string().nullable(),
  /** Where clicking goes. An in-app path, always. */
  url: z.string(),
  /** A short state word: `offline`, `paid`, `disabled`. */
  badge: z.string().nullable(),
  /**
   * How well this matched, so the client can keep the server's order without
   * re-deriving it: 3 exact, 2 starts-with, 1 contains.
   */
  score: z.number().int().min(1).max(3),
});
export type SearchHit = z.infer<typeof searchHitSchema>;

export const searchResultsSchema = z.object({
  /** What was searched for, echoed so a stale response can be discarded. */
  query: z.string(),
  groups: z.array(
    z.object({
      type: z.enum(SEARCH_HIT_TYPES),
      label: z.string(),
      hits: z.array(searchHitSchema),
      /**
       * True when this type had more matches than the per-type cap. The count
       * itself is deliberately NOT returned: "1 of 400" tells somebody how
       * many subscribers a tenant has, which is exactly the kind of fact this
       * feature must not leak sideways.
       */
      more: z.boolean(),
    }),
  ),
});
export type SearchResults = z.infer<typeof searchResultsSchema>;

/**
 * The shortest query worth running.
 *
 * One character matches most of a fleet and costs a full scan per source to
 * say so. Two is the point where an operator is actually naming something.
 */
export const SEARCH_MIN_LENGTH = 2;

/** How many hits one type may contribute, so no type crowds out the rest. */
export const SEARCH_PER_TYPE = 5;

/**
 * How well `term` matches `value` — 3 exact, 2 prefix, 1 contains, 0 never.
 *
 * Shared because the ordering has to be the same on both sides: the server
 * sorts by it, and the client renders the server's order rather than sorting
 * again with a slightly different idea of "better".
 */
export function searchScore(value: string | null | undefined, term: string): number {
  if (!value) return 0;
  const v = value.toLowerCase();
  const t = term.toLowerCase();
  if (!t) return 0;
  if (v === t) return 3;
  if (v.startsWith(t)) return 2;
  return v.includes(t) ? 1 : 0;
}
